import { useEffect, useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";

/**
 * The tier-2 write confirmation: type the target's name, then confirm.
 *
 * `ConfirmDialog` is tier-1's shape and deliberately has no typed field — see
 * its own doc comment. This is the other half: for a write that can break a
 * live merchant integration (revoking a key, rotating a signing secret,
 * changing Shopify credentials), the confirm button stays disabled until the
 * operator types the target's actual identifier, not a fixed word like
 * "CONFIRM". Typing a fixed word becomes muscle memory in a week; typing the
 * merchant's own name forces a look at what is about to happen to.
 *
 * There is still no authorization boundary here — every staff member can
 * still do this. The friction and the audit row it produces are the control.
 */
export function TypedConfirmDialog({
  open,
  onOpenChange,
  title,
  consequence,
  body,
  targetLabel,
  expectedValue,
  confirmLabel,
  pending = false,
  confirmDisabled = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  /** The specific, plain-language consequence of this action. Not "this cannot
   *  be undone" -- a sentence nobody reads a second time. */
  consequence: React.ReactNode;
  /** Extra context above the typed field: a count, a last-used timestamp, a
   *  reason field. */
  body?: React.ReactNode;
  /** What kind of value is being typed, e.g. "storefront name". */
  targetLabel: string;
  /** The exact string that must be typed for the confirm button to enable. */
  expectedValue: string;
  confirmLabel: string;
  pending?: boolean;
  /** An extra gate on top of the typed match — e.g. a required reason field
   *  in `body`. Confirm stays disabled while true, same as a mismatched
   *  value. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");

  // Reset for the next target. Without this, confirming a second action would
  // open with the previous target's matched value still filled in.
  useEffect(() => {
    if (open) setTyped("");
  }, [open, expectedValue]);

  const matches = typed === expectedValue;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // A write already in flight cannot be called back, so closing the
        // dialog mid-request would only hide it. Escape and Cancel otherwise
        // always work -- unlike the one-time secret reveal, there is nothing
        // here to lose by backing out.
        if (pending && !next) return;
        onOpenChange(next);
      }}
      disablePointerDismissal
    >
      <DialogContent showClose={!pending}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{consequence}</DialogDescription>
        </DialogHeader>

        {body ? <div className="space-y-3 text-sm">{body}</div> : null}

        <div className="space-y-1.5">
          <label className="text-sm text-muted-foreground" htmlFor="typed-confirm-input">
            Type &quot;{expectedValue}&quot; ({targetLabel}) to confirm:
          </label>
          <Input
            id="typed-confirm-input"
            autoComplete="off"
            autoFocus
            value={typed}
            disabled={pending}
            onChange={(event) => setTyped(event.target.value)}
            onKeyDown={(event) => {
              // No confirm-by-Enter: this input is not inside a form, but an
              // Enter keypress would still be a keyboard-native "submit"
              // signal a screen reader user might reasonably expect to work.
              // It must not, even with a correct match already typed.
              if (event.key === "Enter") event.preventDefault();
            }}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" disabled={pending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={!matches || pending || confirmDisabled}
            onClick={onConfirm}
            className={cn((!matches || confirmDisabled) && "cursor-not-allowed")}
          >
            {pending ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
